import apiClient from '../index';

export const createSession = async (sessionData) => {
  // Declare formattedData outside try block so it's available in catch block
  let formattedData;

  try {
    // Validate input data
    if (!sessionData.title?.trim()) {
      throw new Error('Session name is required');
    }
    if (!sessionData.startDate) {
      throw new Error('Start date is required');
    }
    if (!sessionData.endDate) {
      throw new Error('End date is required');
    }

    // Format dates and ensure they're valid
    const startDate = new Date(sessionData.startDate);
    const endDate = new Date(sessionData.endDate);

    if (isNaN(startDate.getTime())) {
      throw new Error('Invalid start date');
    }
    if (isNaN(endDate.getTime())) {
      throw new Error('Invalid end date');
    }

    // Create a clean payload with only the required session fields
    formattedData = {
      name: sessionData.title.trim(),
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    console.log('Sending session creation request:', formattedData);

    const response = await apiClient.post('/api/session/', formattedData);
    console.log('Session creation response:', response.data);
    return response.data;
  } catch (error) {
    // Enhanced error logging
    console.error('Session creation error:', {
      originalData: sessionData,
      formattedData,
      error: error.response?.data || error.message,
      stack: error.stack,
    });
    throw error;
  }
};

export const getSession = async (sessionId) => {
  if (!sessionId) throw new Error('Session ID is required');

  try {
    const response = await apiClient.get(`/api/session/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching session with ID ${sessionId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

export const updateSession = async ({ sessionId, data }) => {
  if (!sessionId) throw new Error('Session ID is required');
  if (!data) throw new Error('Update data is required');

  try {
    // Log request details for debugging
    console.log('Updating session with ID:', sessionId);
    console.log('Update payload:', data);

    const formattedData = {
      name: data.name,
      startDate: new Date(data.startDate).toISOString(),
      endDate: new Date(data.endDate).toISOString(),
    };

    const response = await apiClient.put(
      `/api/session/${sessionId}`,
      formattedData
    );

    // Log response for debugging
    console.log('Update response:', response.data);

    if (!response.data) {
      throw new Error('No data received from server');
    }

    // Handle both array and single object responses
    return Array.isArray(response.data) ? response.data[0] : response.data;
  } catch (error) {
    // Enhanced error logging
    console.error('Session update error:', {
      sessionId,
      error: error.response?.data || error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
    });

    // Rethrow with more specific error message
    if (error.response?.status === 404) {
      throw new Error(`Session not found (ID: ${sessionId})`);
    }
    throw error;
  }
};

export const deleteSession = async (sessionId) => {
  if (!sessionId) throw new Error('Session ID is required');

  try {
    const response = await apiClient.delete(`/api/session/${sessionId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Session not found');
    }
    console.error(
      `Error deleting session with ID ${sessionId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

export const getAllSessions = async () => {
  try {
    const response = await apiClient.get('/api/session/');
    return response.data;
  } catch (error) {
    console.error(
      'Error fetching all sessions:',
      error.response?.data || error.message
    );
    throw error;
  }
};
